import { type ErrorRecord } from 'src/hooks/useForm';

import { AUSTIN_ONLY_WEBHOOK_EVENTS } from './constants';
import {
  ACCOMMODATION_TYPE_IDS,
  type IndexedSiteWebhookFormEntry,
  type SiteWebhookCreateOrUpdate,
  type SiteWebhookFormEntry,
} from './types';

const webhookIsBlankAndNotPreexisting = (webhookEntry: SiteWebhookFormEntry) => {
  // webhooks with IDs from backend are not considered blank so errors can be displayed if user tries to clear their
  // fields and save
  return !webhookEntry.webhookUrl && !webhookEntry.accommodationTypeId && !webhookEntry.id;
};

/** map to include overall index so errors can be associated with the correct fields
 * exclude unchanged existing webhooks and new blank rows from updates */
export const indexAndSelectNewOrUpdatedWebhooks = (
  siteWebhookFormEntries: SiteWebhookFormEntry[],
): IndexedSiteWebhookFormEntry[] => {
  return siteWebhookFormEntries
    .map((webhook, index) => {
      return { webhook, index };
    })
    .filter(({ webhook }) => webhook.newOrUpdated && !webhookIsBlankAndNotPreexisting(webhook));
};

/** Updates webhooks in-place to set AccommodationType to AUSTIN on relevant webhook events for which there is no
 * accommodation type Select */
export const setAustinAccommodationTypeOnRelevantEventIds = (indexedWebhooks: IndexedSiteWebhookFormEntry[]): void => {
  indexedWebhooks
    .filter(({ webhook }) => webhook.webhookEventId && AUSTIN_ONLY_WEBHOOK_EVENTS.includes(webhook.webhookEventId))
    .forEach(({ webhook }) => {
      webhook.accommodationTypeId = ACCOMMODATION_TYPE_IDS.AUSTIN;
    });
};

export const validateWebhook = (webhook: SiteWebhookFormEntry): ErrorRecord<SiteWebhookFormEntry> => {
  const rowErrors: ErrorRecord<SiteWebhookFormEntry> = {};

  if (!webhook.webhookUrl) {
    rowErrors.webhookUrl = { error: true, errorMessage: 'Must provide a webhook URL' };
  }
  if (!webhook.accommodationTypeId) {
    rowErrors.accommodationTypeId = { error: true, errorMessage: 'Must select accommodation type' };
  }

  // check for edge case
  const userTriedToDeleteByClearingFields =
    webhook.id && // webhook entry originated from backend
    !webhook.webhookUrl; // url field cleared

  if (userTriedToDeleteByClearingFields) {
    rowErrors.webhookUrl = { error: true, errorMessage: 'Must click Remove button to delete a webhook' };
  }

  return rowErrors;
};

export const webhookFormEntryToPostObject = (webhook: SiteWebhookFormEntry): SiteWebhookCreateOrUpdate => {
  // addWebhook and validateWebhooks ensure required properties are not null.
  // We still verify other aspects of the type conversion instead of blindly casting the type
  return {
    id: webhook.id,
    siteId: webhook.siteId!,
    webhookEventId: webhook.webhookEventId!,
    accommodationTypeId: webhook.accommodationTypeId!,
    webhookUrl: webhook.webhookUrl!,
  };
};
